import axios from "axios";
import {type SupportRequestReceipt, supportRequestReceiptOf} from "./supportRequestReceipt";

/**
 * What the api routes a request as (`SupportRequestCategory` in transferify-api-service).
 *
 * The api defaults an omitted category to `GENERAL`, so this stays optional — but the withdrawal path
 * MUST send `EU_WITHDRAWAL`, because that value is what makes the api acknowledge receipt to the
 * consumer. A statutory declaration filed without it is delivered to support and never acknowledged.
 */
export type SupportRequestCategory = "GENERAL" | "EU_WITHDRAWAL";

/**
 * POST /support/request body (kitecorp/transferify-api-service#101).
 *
 * No identity field: the endpoint is AUTHENTICATED and the api reads the requester from
 * the Firebase bearer token the axios interceptor already attaches. A `replyTo` here would be a
 * second, spoofable answer to "who is asking" — which is exactly the difference between this endpoint
 * and the deliberately public `/feedback` one (api-service#217).
 */
export interface SupportRequest {
	subject: string;
	message: string;
	category?: SupportRequestCategory;
}

/**
 * Support-request service (house `*Api.ts` axios pattern, see `account/AccountApi.ts`). Holds the
 * single HTTP call the {@link useSupportRequest} hook drives, so the dialog view stays dumb (epic #26).
 */
export class SupportRequestApi {
	/**
	 * POSTs the request to `/support/request`. Resolves with the api's receipt on 200, rejects on any
	 * transport/HTTP error — `supportRequestFailure.ts` turns that rejection into the outcome the user
	 * is shown.
	 *
	 * The text is posted VERBATIM: the api measures the raw string its `@Size` bounds are written
	 * against, so trimming here would validate one string and send a different one.
	 */
	static async send(request: SupportRequest): Promise<SupportRequestReceipt> {
		const response = await axios.post("/support/request", request);
		return supportRequestReceiptOf(response.data);
	}
}
